import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CommunicationsTabs } from "./CommunicationsTabs";

interface CommunicationsPageShellProps {
  readonly screenTestId: string;
  readonly titleKey: string;
  readonly subtitleKey: string;
  readonly headerActions?: ReactNode;
  /** Top-level pages (catalog, locales) show the sub-nav; drill-in pages
   *  (detail, translation editor) hide it and rely on a back link. */
  readonly showTabs?: boolean;
  readonly children: ReactNode;
}

/**
 * Shared shell for every `/communications/*` page: eyebrow + title +
 * subtitle, optional sub-nav. The route is gated to `WANDERFIT_ADMIN`
 * (router-level `RequireRole`), so there is no read-only role here — every
 * operator who reaches these pages can edit.
 */
export function CommunicationsPageShell({
  screenTestId,
  titleKey,
  subtitleKey,
  headerActions,
  showTabs = false,
  children,
}: CommunicationsPageShellProps) {
  const { t } = useTranslation();
  return (
    <div data-testid={screenTestId} className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl space-y-2">
          <p className="brand-eyebrow">{t("communications.eyebrow")}</p>
          <h1 className="text-4xl">{t(titleKey)}</h1>
          <p className="font-sans text-base font-normal text-muted-foreground">{t(subtitleKey)}</p>
        </div>
        {headerActions}
      </header>

      {showTabs && <CommunicationsTabs />}

      {children}
    </div>
  );
}
